// Copyright (c) Meta Platforms, Inc. and affiliates.

'use client';

/**
 * @file SideNavItem.tsx
 * @input Uses React, ReactNode, StyleX, Icon, IconType, useMenuHover
 * @output Exports SideNavItem component and SideNavItemProps
 * @position Core implementation; used inside SideNav children
 *
 * Navigation item with icon, selected state, and nesting.
 *
 * Collapsed items with children open their submenu flyout through
 * `useMenuHover`, the shared hover-intent hook (same one `SideNavHeading` and
 * `TopNavMenu` use). Hover is a progressive enhancement over the popover's
 * click behavior and is inert on coarse pointers.
 *
 * SYNC: When modified, update these files to stay in sync:
 * - /packages/core/src/SideNav/SideNav.doc.mjs
 * - /packages/core/src/SideNav/SideNav.test.tsx
 * - /packages/core/src/SideNav/index.ts
 * - /apps/storybook/stories/SideNav.stories.tsx
 * - /packages/cli/assets/templates/blocks/components/SideNav/ (showcase blocks)
 */
import { useCallback, useId, useMemo, useRef, useState } from 'react';
import * as stylex from '@stylexjs/stylex';
import "../theme/tokens.stylex.js";
import { colorVars, spacingVars, sizeVars, fontWeightVars, typeScaleVars, durationVars, easeVars, radiusVars } from "../theme/tokens.stylex.js";
import { Icon, renderIconSlot } from "../Icon/index.js";
import { useLinkComponent } from "../Link/useLinkComponent.js";
import { usePopover } from "../Popover/usePopover.js";
import { useMenuHover } from "../hooks/useMenuHover.js";
import { mergeProps, mergeRefs } from "../utils/index.js";
import { Tooltip } from "../Tooltip/index.js";
import { navItemStyles } from "../NavItem/navItemStyles.stylex.js";
import { focusOutlineProps } from "../utils/focusOutline.stylex.js";
import { useSideNavCollapse, SideNavCollapseContext } from "./SideNavCollapseContext.js";
import { useSideNavRenderMode } from "./SideNavRenderContext.js";
import { useAppShellMobile } from "../AppShell/AppShellMobileContext.js";
import { themeProps } from "../utils/themeProps.js";
import { useTranslator } from "../i18n/index.js";

// =============================================================================
// Styles
// =============================================================================
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
const styles = {
  root: {
    k1xSpc: "x78zum5",
    kXwgrk: "xdt5ytf",
    kzqmXN: "xh8yej3",
    $$css: true
  },
  itemCollapsed: {
    kjj79g: "xl56j7k",
    kzqmXN: "x805d0l",
    kg3NbH: "xnjsko4",
    $$css: true
  },
  itemCollapsedSm: {
    kzqmXN: "x1fvqzum",
    $$css: true
  },
  itemCollapsedLg: {
    kzqmXN: "x1m747yf",
    $$css: true
  },
  expandChevron: {
    k1xSpc: "x3nfvp2",
    kGNEyG: "x6s0dn4",
    kjj79g: "xl56j7k",
    kzqmXN: "x1mtvzj0",
    kZKoxP: "xvpftlf",
    kGuDYH: "x1qlqyl8",
    k1ekBW: "x11xpdln",
    kIyJzY: "xuedmi6 x12w9bfk",
    kAMwcw: "xlr8y92",
    kmuXW: "x2lah0s",
    $$css: true
  },
  expandChevronExpanded: {
    k3aq6I: "x19jd1h0",
    $$css: true
  },
  expandToggle: {
    k1xSpc: "x3nfvp2",
    kGNEyG: "x6s0dn4",
    kjj79g: "xl56j7k",
    kmuXW: "x2lah0s",
    kmVPX3: "x1717udv",
    kogj98: "x1ghz6dp",
    kMzoRj: "xc342km",
    ksu8eU: "xng3xce",
    kWkggS: "xjbqb8w",
    kMwMTN: "x1heor9g",
    kkrTdU: "x1ypdohk",
    kaIpWk: "xh6dtrn",
    kHE3J0: "xe9uy6x",
    kSReZ0: "xyxi2l3",
    $$css: true
  },
  splitAction: {
    k1xSpc: "x78zum5",
    kGNEyG: "x6s0dn4",
    kSGwAc: "xkh2ocl",
    kOIVth: "x1txdalj",
    kUk6DE: "x98rzlu",
    k7Eaqz: "xeuugli",
    kMwMTN: "x1heor9g",
    kybGjl: "x1hl2dhg",
    kmVPX3: "x1717udv",
    kogj98: "x1ghz6dp",
    kMzoRj: "xc342km",
    ksu8eU: "xng3xce",
    kWkggS: "xjbqb8w",
    kMv6JI: "xjb2p0i",
    kGuDYH: "x1qlqyl8",
    k63SB2: "x1pd3egz",
    kLWn49: "x15bjb6t",
    k9WMMc: "x1yc453h",
    kkrTdU: "x1ypdohk",
    $$css: true
  },
  popoverGap: {
    keTefX: "x11g1kdw",
    k71WvV: "xnur1sd",
    $$css: true
  }
};

// Non-collapsed state for popover children — ensures nested items render expanded
const EXPANDED_COLLAPSE_STATE = {
  isCollapsed: false,
  toggle: () => {},
  isCollapsible: false
};

// =============================================================================
// NavItemElement — resolves link vs button based on props
// =============================================================================

/**
 * Renders `<a>` (via LinkComponent) when `href` is set, otherwise `<button>`.
 * Centralizes the link-vs-button decision used across all SideNavItem paths.
 */
function NavItemElement({
  href,
  as,
  isDisabled,
  onClick,
  ref,
  children,
  ...rest
}) {
  const LinkComponent = useLinkComponent(as);
  if (href && !isDisabled) {
    return /*#__PURE__*/_jsx(LinkComponent, {
      ref: ref,
      href: href,
      onClick: onClick,
      ...rest,
      children: children
    });
  }
  return /*#__PURE__*/_jsx("button", {
    ref: ref,
    type: "button",
    onClick: onClick,
    disabled: isDisabled,
    ...rest,
    children: children
  });
}

// =============================================================================
// Types
// =============================================================================

// =============================================================================
// Component
// =============================================================================

/**
 * Navigation item for SideNav.
 *
 * Supports icons, selected state, nesting, and end content like badges or counts.
 *
 * @example
 * ```
 * <SideNavItem
 *   label="Dashboard"
 *   icon={HomeIcon}
 *   selectedIcon={HomeIconSolid}
 *   isSelected
 *   href="/dashboard"
 * />
 * <SideNavItem label="Settings" icon={CogIcon}>
 *   <SideNavItem label="General" href="/settings/general" />
 *   <SideNavItem label="Security" href="/settings/security" />
 * </SideNavItem>
 * ```
 */
export function SideNavItem({
  as,
  label,
  icon,
  selectedIcon,
  isSelected = false,
  isDisabled = false,
  href,
  onClick,
  endContent,
  children,
  collapsible: itemCollapsible,
  size = 'md',
  'data-testid': testId,
  ref,
  xstyle,
  ...rest
}) {
  const t = useTranslator();
  const {
    isCollapsed
  } = useSideNavCollapse();
  const renderMode = useSideNavRenderMode();
  const {
    closeMobileNav
  } = useAppShellMobile();
  const isInDrawer = renderMode === 'drawer' || renderMode === 'drawer-content';
  const id = useId();
  const hasChildren = !!children;
  const itemRef = useRef(null);

  // Popover for collapsed items with children
  const popover = usePopover({
    hasLightDismiss: true,
    hasAutoFocus: true,
    hasCloseButton: false,
    dialogLabel: t('@astryx.sideNavItem.submenuLabel', {
      label
    })
  });

  // Collapse state for items with children
  const itemCollapsibleConfig = useMemo(() => typeof itemCollapsible === 'object' ? itemCollapsible : {}, [itemCollapsible]);
  const isItemCollapsible = hasChildren && itemCollapsible !== false;
  const itemControlledCollapsed = itemCollapsibleConfig.isCollapsed;
  const isItemControlled = itemControlledCollapsed !== undefined;
  const [uncontrolledCollapsed, setUncontrolledCollapsed] = useState(itemCollapsibleConfig.defaultIsCollapsed ?? false);
  const isItemCollapsed = isItemControlled ? itemControlledCollapsed : uncontrolledCollapsed;
  const toggleItemCollapse = useCallback(() => {
    const next = !isItemCollapsed;
    if (!isItemControlled) {
      setUncontrolledCollapsed(next);
    }
    itemCollapsibleConfig.onCollapsedChange?.(next);
  }, [isItemCollapsed, isItemControlled, itemCollapsibleConfig]);
  const displayIcon = isSelected && selectedIcon ? selectedIcon : icon;

  // When collapsible + a primary action (href or onClick), the action and
  // toggle are independent: clicking the label navigates/fires onClick,
  // clicking the chevron expands/collapses children.
  const hasPrimaryAction = !!href || !!onClick;
  const hasIndependentToggle = isItemCollapsible && hasPrimaryAction && !isCollapsed;
  const handleClick = e => {
    if (isDisabled) {
      e.preventDefault();
      return;
    }
    if (isItemCollapsible && !hasIndependentToggle && !isCollapsed) {
      e.preventDefault();
      toggleItemCollapse();
      return;
    }
    onClick?.(e);
    // Close the mobile nav when a nav item is activated inside the drawer
    if (isInDrawer) {
      closeMobileNav();
    }
  };
  const handleToggleClick = e => {
    e.preventDefault();
    e.stopPropagation();
    toggleItemCollapse();
  };

  // Pointer half only. The hook's `onKeyDown`/`menuRef` drive a `useListFocus`
  // over `[role="menuitem"]`, and this flyout is a focus-trapped dialog of
  // links — wiring them would swallow arrow keys rather than navigate with
  // them. Keyboard stays with `usePopover`'s trap, as in DropdownMenuSubMenu.
  const {
    triggerProps: hoverTriggerProps,
    contentProps: hoverContentProps
  } = useMenuHover({
    show: popover.show,
    hide: popover.hide,
    isOpen: popover.isOpen,
    isEnabled: isCollapsed && hasChildren,
    // Standard popover toggling: the flyout opens beside the rail, not over
    // the icon, so the click after a hover-open is a deliberate dismissal
    // rather than the #3121 confirmation the nav menus need.
    clickGuardMs: 0,
    ownsFocus: false
  });

  // In collapsed mode: hide items without icons
  if (isCollapsed && !icon) {
    return null;
  }

  // =========================================================================
  // Collapsed mode — icon-only items, popover for items with children
  // =========================================================================
  if (isCollapsed) {
    const collapsedIcon = displayIcon && renderIconSlot(displayIcon, {
      size: 'sm',
      // `inherit` so a selected row's icon follows the row to HighlightText
      // under forced colors. Identical to `primary` otherwise: both token
      // families are emitted from one expression.
      color: isSelected ? 'inherit' : isDisabled ? 'disabled' : 'secondary'
    });

    // Shared collapsed item styles — used by trigger, link, and button.
    // All three render a focusable element, so each draws the shared ring.
    const collapsedItemStyles = mergeProps(themeProps('side-nav-item', {
      size,
      selected: isSelected ? 'selected' : null,
      disabled: isDisabled ? 'disabled' : null
    }), focusOutlineProps.focusVisible(navItemStyles.item, navItemStyles[size], styles.itemCollapsed, size === 'sm' && styles.itemCollapsedSm, size === 'lg' && styles.itemCollapsedLg, isSelected && navItemStyles.selected, isDisabled && navItemStyles.disabled));

    // Items with children: popover trigger + popover
    if (hasChildren) {
      return /*#__PURE__*/_jsxs("div", {
        ...stylex.props(styles.root, xstyle),
        children: [/*#__PURE__*/_jsx("button", {
          ref: mergeRefs(ref, popover.triggerRef),
          type: "button",
          ...rest,
          ...hoverTriggerProps,
          "aria-label": label,
          "data-testid": testId,
          ...popover.triggerProps,
          ...collapsedItemStyles,
          children: collapsedIcon
        }), popover.render(/*#__PURE__*/_jsxs("div", {
          ...{
            className: "x1hviunn xu0wf1k x7a5moj xfb3i0g"
          },
          onMouseEnter: hoverContentProps.onMouseEnter,
          onMouseLeave: hoverContentProps.onMouseLeave,
          onClick: () => popover.hide(),
          children: [/*#__PURE__*/_jsx("div", {
            ...{
              className: "xf314gf xu0wf1k x141an7d x2mo6ok xv1l7n4 x1ltkj2j"
            },
            children: label
          }), /*#__PURE__*/_jsx(SideNavCollapseContext, {
            value: EXPANDED_COLLAPSE_STATE,
            children: children
          })]
        }), {
          placement: 'end',
          alignment: 'start',
          xstyle: styles.popoverGap
        })]
      });
    }

    // Items without children: icon-only link/button with tooltip
    const collapsedAriaProps = {
      'aria-current': isSelected ? 'page' : undefined,
      'aria-disabled': isDisabled || undefined,
      'aria-label': label,
      'data-testid': testId
    };
    const collapsedElement = /*#__PURE__*/_jsx(NavItemElement, {
      ref: ref,
      href: href,
      as: as,
      isDisabled: isDisabled,
      onClick: handleClick,
      ...rest,
      ...collapsedAriaProps,
      ...collapsedItemStyles,
      children: collapsedIcon
    });
    return /*#__PURE__*/_jsxs("div", {
      ref: itemRef,
      ...stylex.props(styles.root, xstyle),
      children: [collapsedElement, /*#__PURE__*/_jsx(Tooltip, {
        content: label,
        placement: "end",
        anchorRef: itemRef
      })]
    });
  }
  const itemContent = /*#__PURE__*/_jsxs(_Fragment, {
    children: [displayIcon && renderIconSlot(displayIcon, {
      size: 'sm',
      // `inherit` — see the collapsed path above.
      color: isSelected ? 'inherit' : isDisabled ? 'disabled' : 'secondary'
    }), !isCollapsed && /*#__PURE__*/_jsx("span", {
      ...{
        className: "x98rzlu xeuugli xb3r6kr xlyipyv xuxw1ft"
      },
      children: label
    }), !isCollapsed && endContent && /*#__PURE__*/_jsx("span", {
      ...{
        className: "x2lah0s x78zum5 x6s0dn4"
      },
      children: endContent
    }), !isCollapsed && isItemCollapsible && !hasIndependentToggle && /*#__PURE__*/_jsx(Icon, {
      icon: "chevronDown",
      size: "lg",
      color: "inherit",
      xstyle: [styles.expandChevron, !isItemCollapsed && styles.expandChevronExpanded]
    })]
  });
  const itemThemeProps = themeProps('side-nav-item', {
    size,
    selected: isSelected ? 'selected' : null,
    disabled: isDisabled ? 'disabled' : null
  });
  const itemStyleArgs = [navItemStyles.item, navItemStyles[size], isSelected && navItemStyles.selected, isDisabled && navItemStyles.disabled];

  // Two shapes of the same row appearance:
  // - `rowProps` for the split-action path, where the row is a plain <div>
  //   container and its children take focus (so the ring belongs on them);
  // - `focusableRowProps` for every other path, where the row element is
  //   itself the focusable control.
  const rowProps = mergeProps(itemThemeProps, stylex.props(...itemStyleArgs));
  const focusableRowProps = mergeProps(itemThemeProps, focusOutlineProps.focusVisible(...itemStyleArgs));

  // Split-action path: collapsible plus a primary action renders the primary
  // element and the chevron toggle as siblings in a <div> row, since a
  // <button> cannot nest inside an <a>.
  let itemElement;
  if (hasIndependentToggle) {
    itemElement = /*#__PURE__*/_jsxs("div", {
      "data-testid": testId,
      ...rowProps,
      children: [/*#__PURE__*/_jsx(NavItemElement, {
        ref: ref,
        href: href,
        as: as,
        isDisabled: isDisabled,
        onClick: handleClick,
        ...rest,
        "aria-current": isSelected ? 'page' : undefined,
        ...focusOutlineProps.focusVisible(styles.splitAction),
        children: itemContent
      }), /*#__PURE__*/_jsx("button", {
        type: "button",
        onClick: handleToggleClick,
        "aria-label": isItemCollapsed ? t('@astryx.sideNavItem.expand', {
          label
        }) : t('@astryx.sideNavItem.collapse', {
          label
        }),
        "aria-expanded": !isItemCollapsed,
        "aria-controls": `${id}-children`,
        ...focusOutlineProps.focusVisible(styles.expandToggle),
        children: /*#__PURE__*/_jsx(Icon, {
          icon: "chevronDown",
          size: "lg",
          color: "inherit",
          xstyle: [styles.expandChevron, !isItemCollapsed && styles.expandChevronExpanded]
        })
      })]
    });
  } else {
    const ariaProps = {
      'aria-current': isSelected ? 'page' : undefined,
      'aria-disabled': isDisabled || undefined,
      'aria-expanded': isItemCollapsible ? !isItemCollapsed : undefined,
      'aria-controls': isItemCollapsible ? `${id}-children` : undefined,
      'data-testid': testId
    };
    itemElement = /*#__PURE__*/_jsx(NavItemElement, {
      ref: ref,
      href: href,
      as: as,
      isDisabled: isDisabled,
      onClick: handleClick,
      ...rest,
      ...ariaProps,
      ...focusableRowProps,
      children: itemContent
    });
  }
  const item = /*#__PURE__*/_jsxs("div", {
    ref: itemRef,
    ...stylex.props(styles.root, xstyle),
    children: [itemElement, hasChildren && !isCollapsed && /*#__PURE__*/_jsx("div", {
      id: `${id}-children`,
      role: "group",
      "aria-labelledby": `${id}-label`,
      "aria-hidden": isItemCollapsed,
      inert: isItemCollapsed ? true : undefined,
      ...{
        0: {
          className: "xrvj5dj x1tu4anv x1qn9uv2 x80gvsz x12w9bfk xlr8y92"
        },
        1: {
          className: "xrvj5dj x1qn9uv2 x80gvsz x12w9bfk xlr8y92 xihq33y"
        }
      }[!!isItemCollapsed << 0],
      children: /*#__PURE__*/_jsxs("div", {
        ...{
          className: "xb3r6kr x2lwn1j x31w388"
        },
        children: [/*#__PURE__*/_jsx("span", {
          id: `${id}-label`,
          hidden: true,
          children: label
        }), children]
      })
    })]
  });
  return item;
}
SideNavItem.displayName = 'SideNavItem';